import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

const FloatingElementPortal = ({
  children,
  mode = "horizontal",
  margin = 10,
  xMargin = 0,
  yMargin = 0,
  zIndex = 1000,
  onClose, // Added onClose prop
}) => {
  const parentRef = useRef(null);
  const listRef = useRef(null);
  const [positionStyles, setPositionStyles] = useState({});

  /**
   * Function to update the position of the floating list dynamically.
   */
  const updatePosition = () => {
    if (parentRef.current && listRef.current) {
      const parentRect = parentRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const listWidth = listRef.current.offsetWidth;
      const listHeight = listRef.current.offsetHeight;

      let styles = {};

      if (mode === "horizontal") {
        // Default: Open to the right
        styles.left = parentRect.right + margin + xMargin;
        styles.top = parentRect.top + yMargin;

        // If there's not enough space on the right, switch to the left
        if (styles.left + listWidth > viewportWidth) {
          styles.left = parentRect.left - listWidth - margin - xMargin;
        }

        // Ensure the list fits within the vertical bounds
        if (styles.top + listHeight > viewportHeight) {
          styles.top = viewportHeight - listHeight + yMargin;
        } else if (styles.top < 0) {
          styles.top = 0;
        }
      } else if (mode === "vertical") {
        // Default: Open to the bottom
        styles.top = parentRect.bottom + margin + yMargin;
        styles.left = parentRect.left + xMargin;

        // If there's not enough space below, switch to the top
        if (styles.top + listHeight > viewportHeight) {
          styles.top = parentRect.top - listHeight - margin - yMargin;
        }

        // Ensure the list fits within the horizontal bounds
        if (styles.left + listWidth > viewportWidth) {
          styles.left = viewportWidth - listWidth;
        } else if (styles.left < 0) {
          styles.left = 0;
        }
      }

      setPositionStyles(styles);
    }
  };

  /**
   * Attach event listeners for dynamic positioning.
   */
  useLayoutEffect(() => {
    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("mousedown", handleOutsideClick); // Added event listener for outside click

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  /**
   * Handle outside click to trigger onClose
   */
  const handleOutsideClick = (e) => {
    if (
      listRef.current &&
      !listRef.current.contains(e.target) &&
      parentRef.current &&
      !parentRef.current.contains(e.target)
    ) {
      onClose?.(); // Call onClose when clicking outside
    }
  };

  return (
    <div ref={parentRef} style={{ display: "inline-block", position: "relative" }}>
      {/* Parent Wrapper */}
      {children.trigger}

      {/* Floating List rendered in portal */}
      {ReactDOM.createPortal(
        <div
          ref={listRef}
          style={{
            position: "fixed",
            ...positionStyles,
            zIndex: zIndex,
          }}
        >
          {children.list ? children.list : children}
        </div>,
        document.body
      )}
    </div>
  );
};

export default FloatingElementPortal;

{
  /*
  ***************Usage ***************
  
  <FloatingList mode="vertical">
{{
  trigger: (
    <button onClick={() => setIsOpen(!isOpen)}>
      Toggle List
    </button>
  ),
  list: isOpen && (
    <div style={{ background: "white", padding: "10px", border: "1px solid #ccc" }}>
      <p>Item 1</p>
      <p>Item 2</p>
      <p>Item 3</p>
    </div>
  )
}}
</FloatingList> 

*/
}
